import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import TentSponsorManagement from '../../components/TentSponsorManagement';
import ImageUploadField from '../../components/ImageUploadField';
import { API_URL } from './adminUtils';
import {
  Calendar,
  Clock,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Edit,
  Trash2,
  Star,
  Tent,
  X,
  CircleCheck,
} from 'lucide-react';

const ActivitiesManagement = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [editingActivity, setEditingActivity] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  
  useEffect(() => {
    fetchActivities();
  }, []);
  
  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API_URL}/activities`);
      setActivities(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  
  const handleStatusChange = async (activityId, status) => {
    try {
      await axios.put(`${API_URL}/activities/${activityId}/status`, { status });
      const messages = {
        approved: 'Activity approved! Date is now blocked on calendar.',
        rejected: 'Activity rejected.',
        completed: 'Event marked as completed! It will appear in Past Highlights.'
      };
      toast.success(messages[status] || `Activity ${status}`);
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update activity');
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    try {
      await axios.delete(`${API_URL}/activities/${activityId}`);
      toast.success('Activity deleted');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to delete activity');
    }
  };
  
  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setEditFormData({
      title: activity.title || '',
      description: activity.description || '',
      date: activity.date || '',
      end_date: activity.end_date || '',
      time: activity.time || '',
      end_time: activity.end_time || '',
      location: activity.location || '',
      category: activity.category || '',
      image_url: activity.image_url || ''
    });
  };

  const handleToggleFeatured = async (activity) => {
    try {      const newFeatured = !activity.featured;
      await axios.put(`${API_URL}/activities/${activity.id}/featured`, { featured: newFeatured });
      toast.success(newFeatured ? 'Event marked as featured' : 'Event removed from featured');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const handleToggleTentEligible = async (activity) => {
    try {      const newEligible = !activity.tent_eligible;
      await axios.put(`${API_URL}/events/${activity.id}/tent-eligible`, { tent_eligible: newEligible });
      toast.success(newEligible ? 'Tent reservations enabled' : 'Tent reservations disabled');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to update tent eligibility');
    }
  };

  const [managingTentEvent, setManagingTentEvent] = useState(null);
  
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(`${API_URL}/activities/${editingActivity.id}`, editFormData);
      toast.success('Activity updated successfully');
      setEditingActivity(null);
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update activity');
    } finally {
      setSavingEdit(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const isPastEvent = (activity) => (activity.end_date || activity.date) < today;
  
  const filteredActivities = (() => {
    if (filter === 'all') return activities;
    if (filter === 'past') {
      // Show approved events whose date has passed (not yet marked completed) + completed events
      return activities.filter(a => 
        a.status === 'completed' || (a.status === 'approved' && isPastEvent(a))
      );
    }
    if (filter === 'approved') {
      // Only show future/current approved events (past ones go to Past/Completed tab)
      return activities.filter(a => a.status === 'approved' && !isPastEvent(a));
    }
    return activities.filter(a => a.status === filter);
  })();

  const pastCount = activities.filter(a => 
    a.status === 'completed' || (a.status === 'approved' && isPastEvent(a))
  ).length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '';
    const start = formatDate(startDate);
    if (!endDate || endDate === startDate) return start;
    const end = new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} - ${end}`;
  };

  const getStatusBadge = (activity) => {
    if (activity.status === 'completed') {
      return <Badge className="bg-blue-600 text-white" data-testid={`completed-badge-${activity.id}`}>Completed</Badge>;
    }
    if (activity.status === 'approved' && isPastEvent(activity)) {
      return <Badge className="bg-amber-600 text-white" data-testid={`past-badge-${activity.id}`}>Past</Badge>;
    }
    const classes = {
      approved: 'bg-cameroon-green text-white',
      rejected: 'bg-destructive',
      pending: 'bg-cameroon-yellow text-black',
    };
    return <Badge className={classes[activity.status] || ''}>{activity.status}</Badge>;
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold">Activity & Event Management</h1>
          <p className="text-muted-foreground">Review, approve, and manage event lifecycle. Mark past events as completed.</p>
        </div>
        <Button variant="outline" onClick={fetchActivities}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="pending" className="gap-2" data-testid="filter-pending">
            Pending
            <Badge variant="secondary" className="bg-cameroon-yellow/20 text-cameroon-yellow">
              {activities.filter(a => a.status === 'pending').length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="filter-approved">
            Approved ({activities.filter(a => a.status === 'approved' && !isPastEvent(a)).length})
          </TabsTrigger>
          <TabsTrigger value="past" className="gap-2" data-testid="filter-past">
            Past / Completed
            <Badge variant="secondary" className="bg-blue-600/20 text-blue-600">
              {pastCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="filter-rejected">Rejected ({activities.filter(a => a.status === 'rejected').length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="filter-all">All ({activities.length})</TabsTrigger>
        </TabsList>
      </Tabs>
      
      <div className="space-y-4">
        {filteredActivities.map((activity) => (
          <Card key={activity.id} className={`overflow-hidden ${activity.status === 'completed' ? 'border-blue-200 bg-blue-50/30' : ''}`}>
            <CardContent className="p-0">
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                {(activity.image_url || activity.association_image) && (
                  <div className="md:w-48 h-32 md:h-auto flex-shrink-0">
                    <img 
                      src={activity.image_url || activity.association_image}
                      alt={activity.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="flex-1 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{activity.title}</h3>
                        {getStatusBadge(activity)}
                        {activity.featured && (
                          <Badge className="bg-purple-600 text-white" data-testid={`featured-badge-${activity.id}`}>Featured</Badge>
                        )}
                        {activity.tent_eligible && (
                          <Badge className="bg-teal-600 text-white" data-testid={`tent-badge-${activity.id}`}>Tent Eligible</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">{activity.association_short_name || activity.association_name}</Badge>
                        <Badge variant="outline" className="text-xs">{activity.category}</Badge>
                        {activity.confirmation_number && (
                          <Badge variant="secondary" className="text-xs font-mono bg-cameroon-green/10 text-cameroon-green">
                            #{activity.confirmation_number}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{activity.description}</p>
                      
                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-cameroon-green" />
                          {formatDateRange(activity.date, activity.end_date)}
                          {activity.end_date && activity.end_date !== activity.date && (
                            <Badge variant="secondary" className="ml-1 text-xs">Multi-day</Badge>
                          )}
                        </span>
                        {activity.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-cameroon-yellow" />
                            {activity.time}{activity.end_time && ` - ${activity.end_time}`}
                          </span>
                        )}
                        {activity.location && (
                          <span className="flex items-center gap-1 truncate max-w-[200px]">
                            📍 {activity.location}
                          </span>
                        )}
                      </div>
                      
                      {activity.notice && (
                        <div className="mt-3 p-2 bg-cameroon-yellow/10 rounded text-sm text-muted-foreground">
                          <strong>Notice:</strong> {activity.notice}
                        </div>
                      )}

                      {activity.status === 'completed' && activity.completed_at && (
                        <div className="mt-2 text-xs text-blue-600">
                          Completed on {new Date(activity.completed_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {activity.status === 'pending' && (
                        <>
                          <Button 
                            size="sm"
                            onClick={() => handleStatusChange(activity.id, 'approved')}
                            className="bg-cameroon-green hover:bg-cameroon-green/90"
                            data-testid={`approve-${activity.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm"
                            variant="destructive"
                            onClick={() => handleStatusChange(activity.id, 'rejected')}
                            data-testid={`reject-${activity.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {/* Mark Complete button: show for approved events that are past */}
                      {activity.status === 'approved' && isPastEvent(activity) && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(activity.id, 'completed')}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`complete-${activity.id}`}
                        >
                          <CircleCheck className="w-4 h-4 mr-1" />
                          Mark Complete
                        </Button>
                      )}
                      {(activity.status === 'approved' || activity.status === 'completed') && (
                        <>
                        <Button
                          size="sm"
                          variant={activity.featured ? "default" : "outline"}
                          onClick={() => handleToggleFeatured(activity)}
                          className={activity.featured ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}
                          data-testid={`toggle-featured-${activity.id}`}
                        >
                          <Star className={`w-4 h-4 mr-1 ${activity.featured ? 'fill-current' : ''}`} />
                          {activity.featured ? 'Unfeatured' : 'Feature'}
                        </Button>
                        {activity.status === 'approved' && (
                          <>
                          <Button
                            size="sm"
                            variant={activity.tent_eligible ? "default" : "outline"}
                            onClick={() => handleToggleTentEligible(activity)}
                            className={activity.tent_eligible ? "bg-teal-600 hover:bg-teal-700 text-white" : ""}
                            data-testid={`toggle-tent-${activity.id}`}
                          >
                            <Tent className={`w-4 h-4 mr-1`} />
                            {activity.tent_eligible ? 'Tent On' : 'Tent Off'}
                          </Button>
                          {activity.tent_eligible && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setManagingTentEvent(activity)}
                              data-testid={`manage-tent-${activity.id}`}
                            >
                              Manage Tents
                            </Button>
                          )}
                          </>
                        )}
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditActivity(activity)}
                        data-testid={`edit-activity-${activity.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(activity.id)}
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {filteredActivities.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {filter === 'past' ? 'No past or completed events' : 'No activities found'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Edit Activity Dialog */}
      <Dialog open={!!editingActivity} onOpenChange={(open) => !open && setEditingActivity(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-serif">Edit Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 overflow-y-auto flex-1 pr-1">
            <div className="space-y-2">
              <Label>Event Title</Label>
              <Input 
                value={editFormData.title}
                onChange={(e) => setEditFormData(prev => ({...prev, title: e.target.value}))}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={editFormData.description}
                onChange={(e) => setEditFormData(prev => ({...prev, description: e.target.value}))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input 
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData(prev => ({...prev, date: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Input 
                  type="date"
                  value={editFormData.end_date}
                  onChange={(e) => setEditFormData(prev => ({...prev, end_date: e.target.value}))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input 
                  type="time"
                  value={editFormData.time}
                  onChange={(e) => setEditFormData(prev => ({...prev, time: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input 
                  type="time"
                  value={editFormData.end_time}
                  onChange={(e) => setEditFormData(prev => ({...prev, end_time: e.target.value}))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input 
                value={editFormData.location}
                onChange={(e) => setEditFormData(prev => ({...prev, location: e.target.value}))}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={editFormData.category} onValueChange={(v) => setEditFormData(prev => ({...prev, category: v}))}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cultural Festival">Cultural Festival</SelectItem>
                  <SelectItem value="Meeting">Meeting</SelectItem>
                  <SelectItem value="Workshop">Workshop</SelectItem>
                  <SelectItem value="Celebration">Celebration</SelectItem>
                  <SelectItem value="Community">Community</SelectItem>
                  <SelectItem value="Education">Education</SelectItem>
                  <SelectItem value="Sports">Sports</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Event Image */}
            <div className="space-y-2">
              <Label>Event Photo</Label>
              {editFormData.image_url && (
                <div className="relative w-full h-32 rounded-lg overflow-hidden mb-2 bg-muted">
                  <img src={editFormData.image_url} alt="Event" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setEditFormData(prev => ({...prev, image_url: ''}))}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              )}
              <ImageUploadField
                value={editFormData.image_url}
                onChange={(url) => setEditFormData(prev => ({...prev, image_url: url}))}
                label="Upload or change event photo"
                data-testid="edit-event-image"
              />
            </div>
          </div>
          {/* Sticky action buttons — always visible */}
          <div className="flex justify-end gap-2 pt-4 mt-2 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => setEditingActivity(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit} className="bg-cameroon-green hover:bg-cameroon-green/90">
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tent & Sponsor Management Dialog */}
      <Dialog open={!!managingTentEvent} onOpenChange={(open) => !open && setManagingTentEvent(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tent className="w-5 h-5 text-cameroon-green" />
              Tent & Sponsorship — {managingTentEvent?.title}
            </DialogTitle>
          </DialogHeader>
          {managingTentEvent && <TentSponsorManagement eventId={managingTentEvent.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivitiesManagement;
